import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import {Calendar, Event, momentLocalizer, SlotInfo, View, Views} from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {NewEventModal} from "./NewEventModal";
import {EditEventModal} from "./EditEventModal";
import withDragAndDrop, {EventInteractionArgs} from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import {findClassTemplate, findPractitionerIndex, getColor, getEventType} from "./CalendarUtil";
import {CalendarEventType} from "./EventModal";
import {TabbedPageHeader} from "../../../../components/TabbedPageHeader";
import "./Calendar.css";
import {useGetCalendarEvents} from "../../../../util/RpcHooks/GetCalendarEvents";
import {
  GetEventsRequest,
  IKensuCalendarEventProto,
  KensuCalendarEventProto,
  MutateCalendarEventRequest
} from "../../../../provider_api";
import {useMutateCalendarEvent} from "../../../../util/RpcHooks/MutateCalendarEvent";
import {ConfirmEditPrompt} from "./ConfirmEditPrompt";

// Very important that this is OUTSIDE of the ProviderCalendar definition.
// Otherwise changing the time range on the calendar does not work, it resets
// to exactly the previous date. I don't know what's going on.
const DnDCalendar = withDragAndDrop(Calendar)

const getSignupsText = (count: number | undefined | null) => {
  if (!count) {
    return '0 signups';
  }

  if (count == 1) {
    return '1 signup';
  }

  return `${count} signups`;
}

// This is the page for editing (or adding) a service.
export const ProviderCalendar = () => {

  const {businessProfile, business} = useProviderProfile();
  const localizer = momentLocalizer(moment);

  // This contains the latest list of events. 
  const getCalendarEvents = useGetCalendarEvents();
  const mutateCalendarEvent = useMutateCalendarEvent();

  // react-big-calendar seems to respond better with controlled properties. i was getting bugs
  // where i couldn't change the date when using uncontrolled.
  const [view, setView] = useState<View>(Views.WEEK);
  const [date, setDate] = useState(new Date());

  // The range of the calendar view, used to limit the number of events
  // that are sent down from the server
  const startRange = useRef(moment().startOf('week').toDate());
  const endRange = useRef(moment().endOf('week').toDate());

  // This is the modal overlay, if one exists!  There are different overlays for creating new events
  // (NewEventModal), editing events (EditEventModal), and confirmations (ConfirmEditPrompt).
  // const [overlay, setOverlay] = useState<ReactElement>();
  const [eventTypeFilter, setEventTypeFilter] = useState<CalendarEventType[]>(['appointment', 'availability', 'class']);
  const [providerFilter, setProviderFilter] = useState<string[]>(businessProfile?.practitioners?.map(p => {
    return p.practitionerId as string
  }) || []);

  type OverlayState = 'newevent' | 'editevent' | 'eventresize' | 'confirmedit' | undefined;
  const [overlayState, setOverlayState] = useState<OverlayState>();
  const [newEventSlot, setNewEventSlot] = useState<{ start: Date, end: Date }>(); // when 'newevent' is set, the slot that was clicked
  const [editExistingEvent, setEditExistingEvent] = useState<KensuCalendarEventProto>(); // event being edited.
  const [resizingEvent, setResizingEvent] = useState<EventInteractionArgs<Event>>(); // calendar drag resize

  useEffect(() => {
    getEvents()
  }, [view, date])

  const eventTypeShown = (eventType: CalendarEventType) => {
    return eventTypeFilter.indexOf(eventType) >= 0;
  }

  const getBookingsForClass = (instance: IKensuCalendarEventProto) => {
    if (!instance.class) {
      return [];
    }

    return getCalendarEvents.events.filter(e => {
      return !!e.classBooking && e.classBooking?.booking?.eventInstanceId == instance.id;
    });
  }

  const getTitle = (instance: IKensuCalendarEventProto) => {
    switch ((instance as KensuCalendarEventProto).Type) {

      case "appointment":
        const details = instance.appointment?.booking?.details;
        return details?.service?.serviceName + " with " + details?.clientName

      case "availability":
        return 'Available';

      case "class":
        
        const className = instance.class?.details?.classTemplateId ?
            findClassTemplate(businessProfile, instance.class?.details?.classTemplateId)?.name :
            instance.class?.details?.className;
        
        return `${className} (${getSignupsText(getBookingsForClass(instance).length)})`;
    }

    return '';
  }

  const getProviderForEvent = (instance: IKensuCalendarEventProto) => {
    if (instance.availability) {
      return instance.availability.details?.practitionerId;
    }

    if (instance.appointment) {
      return instance.appointment.booking?.details?.service?.practitionerId;
    }

    if (instance.class) {
      return instance.class.details?.teacherPractitionerId;
    }

    return null;
  }

  const toCalendarEvents = () => {
    let calendarEvents: Event[] = [];
    getCalendarEvents.events.forEach(instance => {

      if (instance.classBooking) {
        return;
      }

      // If this provider's calendar is hidden, don't add the event.
      const eventProvider = getProviderForEvent(instance) as string;
      if (!eventProvider) {
        console.warn('No provider for event', instance);
      }

      const eventType = getEventType(instance);
      if (!eventType) {
        console.warn('Event has no type', instance)
      }

      if (eventProvider && providerFilter.indexOf(eventProvider) < 0) {
        return;
      }

      if (eventType && !eventTypeShown(eventType)) {
        return;
      }

      calendarEvents.push({
        start: new Date(instance.startTimestamp as number),
        end: new Date(instance.endTimestamp as number),
        title: getTitle(instance),
        // Resource is a generic field on the calendar that lets us correlate to the
        // original data. We use the EventInstanceProto2.
        resource: instance
      })
    })
    return calendarEvents;
  }

  const getEvents = () => {
    getCalendarEvents.send(new GetEventsRequest({
      businessId: business?.businessId,
      startTimestampMs: startRange.current.getTime(),
      endTimestampMs: endRange.current.getTime(),
    }));
  }

  /**
   * Handles a single instance update (just this instance, not future instances too).
   *
   * @param existingEventId the event as it previously existed; this is removed from the calendar
   *                        and replaced with any events in the updated array
   *
   * @param updated there should only be a single event in the array, but the API is
   * tolerant and accepts an array. TODO: make this better
   */
  const updateSingleEventFromServer = (
      existingEventId: string, updated: IKensuCalendarEventProto[]) => {

    if (updated.length >= 2) {
      console.warn("Updated event has more than one instance")
    }

    getCalendarEvents.setEvents(events => {
      // Clear out the existing event and then add all updated events, the latter
      // happening only if this is a mutate.
      return [...events.filter(e => e.id != existingEventId), ...updated];
    })
  }

  /**
   * This handles the case of modifying or deleting an event, and all its future
   * instances. All events in the original series are simply deleted from the
   * client cache, and the updated events replace them. For a delete, the events
   * will all have the same event ID; for a modify, there will be 2 series with
   * 2 unique event ids.
   */
  const updateInstanceAndFutureEvents = (existingEventInstanceId: string,
                                         newEvents: IKensuCalendarEventProto[]) => {

    // This is the only place where the combined event/instance ID (12345fafa.2025-05-12)
    // affects the client. We have to get the id of the series to clear out events.
    // We could just do a full refresh in this case otherwise.
    const existingEventId = existingEventInstanceId.substring(0, existingEventInstanceId.indexOf('.'));

    // A prefix query will work just nicely.
    getCalendarEvents.setEvents(events => [
      ...events.filter(e => {
        return !e.id?.startsWith(existingEventId);
      }),
      ...newEvents
    ]);
    setOverlayState(undefined);
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    showNewEventModal(slot.start, slot.end);
  };

  const showNewEventModal = (start: Date, end: Date) => {
    // If another modal is open don't mess with it.
    if (!overlayState) {
      setNewEventSlot({start: start, end: end});
      setOverlayState('newevent');
    }
  }

  const handleSelectEvent = (event: Event) => {
    // If another modal is open don't mess with it.
    if (!overlayState) {
      const existingEvent = event.resource as KensuCalendarEventProto;
      setOverlayState('editevent');
      setEditExistingEvent(existingEvent);
    }
  };

  const getSlotStyle = (): React.HTMLAttributes<HTMLDivElement> => {
    return {
      className: 'slot-enabled',
      style: {}
    }
  }

  /**
   * Sets styles for calendar events. This is a callback from the calendar.
   * Availability blocks look a bit different than appointments.
   */
  const getEventStyle = (event: Event) => {

    const existingEvent = event.resource as KensuCalendarEventProto;

    switch (getEventType(existingEvent)) {
      case 'availability': {
        const eventProvider = existingEvent.availability?.details?.practitionerId;
        const providerColor = getColor(findPractitionerIndex(businessProfile?.practitioners, eventProvider));

        return {
          style: {
            backgroundColor: 'var(--white)',
            borderColor: providerColor,
            color: 'var(--black)',
            borderWidth: 2,
          }
        }
      }

      case "appointment": {
        const eventProvider = existingEvent.appointment?.booking?.details?.service?.practitionerId
        const providerColor = getColor(findPractitionerIndex(businessProfile?.practitioners, eventProvider));
        return {
          style: {
            backgroundColor: providerColor,
            borderColor: providerColor,
            color: 'var(--white)',
            borderWidth: 2,
          }
        }
      }

      case "class": {
        const classEvent = existingEvent.class
        const eventProvider = classEvent?.details?.teacherPractitionerId;
        const providerColor = getColor(findPractitionerIndex(businessProfile?.practitioners, eventProvider));
        return {
          style: {
            backgroundColor: providerColor,
            borderColor: providerColor,
            color: 'var(--white)',
            borderWidth: 2,
          }
        }
      }
      default: {
        return {
          style: {}
        }
      }
    }
  }

  /**
   * This happens on an event drag or resize. If the event is repeating, it has to pop up
   * a modal ("just this event" or "this and future events") before sending the rpc.
   */
  const handleEventResize = useCallback((event: EventInteractionArgs<Event>) => {

    const existingEvent = event.event.resource as KensuCalendarEventProto

    if (existingEvent.class?.repeatStrategy?.weekly ||
        existingEvent.availability?.repeatStrategy?.weekly) {
      setOverlayState('eventresize');
      setResizingEvent(event);
    } else {
      sendUpdateRequest(event, false)
    }
  }, []);

  const sendUpdateRequest = (event: EventInteractionArgs<Event>, updateFutureEvents: boolean) => {

    const existingEvent = event.event.resource as KensuCalendarEventProto

    const request = new MutateCalendarEventRequest({
      clientStartTimestampMs: startRange.current.getTime(),
      clientEndTimestampMs: endRange.current.getTime(),
      includeFutureInstances: updateFutureEvents,
      event: {
        ...existingEvent,
        startTimestamp: (event.start as Date).getTime(),
        endTimestamp: (event.end as Date).getTime(),
      }
    })

    mutateCalendarEvent.send({
      request: request,
      method: 'update_' + getEventType(existingEvent),
      onError: (message) => alert(message),
      onSuccess: (instances) => {
        if (request.includeFutureInstances) {
          // TODO fix this random ID
          updateInstanceAndFutureEvents(existingEvent.id, instances);
        } else {
          updateSingleEventFromServer(existingEvent.id, instances);
        }
        setOverlayState(undefined)
      }
    });
  }

  const toggleProviderShown = (providerId: string) => {
    setProviderFilter(existing => {
      if (existing.indexOf(providerId) < 0) {
        return [...existing, providerId];
      } else {
        return existing.filter(e => e != providerId);
      }
    })
  }

  const toggleEventTypeShown = (eventType: CalendarEventType) => {
    setEventTypeFilter(existing => {
      if (existing.indexOf(eventType) < 0) {
        return [...existing, eventType];
      } else {
        return existing.filter(e => e != eventType);
      }
    })
  }

  // This sets the default scroll position towards the middle, so you are looking at
  // the workday by default and not midnight to 8am.
  const scrollToTime = new Date();
  scrollToTime.setHours(8);
  scrollToTime.setMinutes(0);

  return <div className={'NonScrollingToolPage'}>

    <TabbedPageHeader
        buttons={[{
          text: 'Create Event',
          onClick: () => showNewEventModal(moment().add(1, 'hour').startOf('hour').toDate(),
              moment().add(2, 'hour').startOf('hour').toDate())
        }]}
        pageTitle={`${businessProfile?.proto?.businessName} Calendar`}/>

    <div className={'CalendarMainLayout'}>

      <div className={'CalendarLeftNav'}>
        <h4>Team Members</h4>
        {businessProfile?.practitioners?.filter(p => !p.archived).map((p, i) => {

          let thisProviderId = p.practitionerId as string;
          const providerShown = providerFilter.indexOf(thisProviderId) >= 0;

          // The index into the filtered list of providers may not be the same as the index in the
          // full list of providers... so do not use the index value `i` here.
          const providerColor = getColor(findPractitionerIndex(businessProfile?.practitioners, p.practitionerId));

          return <div className={'CalendarTeamMemberContainer'}
                      key={'prov-' + i}>
            <div className={'CalendarTeamMemberCheckbox'}
                 onClick={() => toggleProviderShown(thisProviderId)}
                 style={{
                   backgroundColor: providerShown ? providerColor : '#fff',
                   borderColor: providerColor,
                 }}/>
            <div>{p.firstName} {p.lastName}</div>
          </div>
        })}

        <h4>Event types</h4>
        {business?.businessSettings?.offersAppointments && <>
          <div className={'CalendarTeamMemberContainer'}>
            <input className={'AppointmentTypeCheckbox'} type={'checkbox'} checked={eventTypeShown('appointment')}
                   onChange={e => toggleEventTypeShown('appointment')}/>
            <div>Appointments</div>
          </div>
          <div className={'CalendarTeamMemberContainer'}>
            <input className={'AppointmentTypeCheckbox'} type={'checkbox'} checked={eventTypeShown('availability')}
                   onChange={e => toggleEventTypeShown('availability')}/>
            <div>Availability</div>
          </div>
        </>}

        {business?.businessSettings?.offersClasses && <>
          <div className={'CalendarTeamMemberContainer'}>
            <input className={'AppointmentTypeCheckbox'} type={'checkbox'} checked={eventTypeShown('class')}
                   onChange={e => toggleEventTypeShown('class')}/>
            <div>Classes</div>
          </div>
        </>}
      </div>

      <DnDCalendar
          date={date}
          onNavigate={(date, view) => {
            console.log('onNavigate', date, view);
            setDate(date);
            setView(view)
          }}
          views={[Views.WEEK, Views.DAY, Views.AGENDA]}
          view={view}
          onView={setView}
          localizer={localizer}
          dayLayoutAlgorithm={'overlap'}
          events={toCalendarEvents()}
          style={{width: '100%', height: '100%', overflowY: 'scroll'}}
          scrollToTime={scrollToTime}
          timeslots={4}
          step={15}
          selectable
          eventPropGetter={getEventStyle}
          slotPropGetter={getSlotStyle}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onEventResize={handleEventResize}
          onEventDrop={handleEventResize}
          onRangeChange={(range) => {

            if (Array.isArray(range)) {

              // The week view returns an array of 7 dates, all at midnight. This is only
              // a 6 day span, so we have to add 24 hours to the latter.
              // 
              // The day view just gives a single date at midnight. Likewise, we need
              // to add 24 hours to this as well to get the end span. 
              const endOfLastDay = new Date(range[range.length - 1]);
              endOfLastDay.setDate(endOfLastDay.getDate() + 1);

              startRange.current = range[0];
              endRange.current = endOfLastDay;

            } else {

              // This should only happen for the "Monthly" or "Agenda" views of react big
              // calendar that we are not using.
              startRange.current = range.start;
              endRange.current = range.end;
            }
          }}/>
    </div>

    {overlayState == 'newevent' && newEventSlot && <NewEventModal
        startTime={newEventSlot.start}
        endTime={newEventSlot.end}
        calendarViewStart={startRange.current}
        calendarViewEnd={endRange.current}
        onCreate={created => {
          getCalendarEvents.setEvents(events => [...events, ...created])
          setOverlayState(undefined);
        }}
        onCancel={() => setOverlayState(undefined)}
    />}

    {overlayState == 'editevent' && editExistingEvent && <EditEventModal
        onClassBookingDeleted={bookingId =>
            getCalendarEvents.setEvents(events => events.filter(e => e.id != bookingId))
        }
        classBookings={getBookingsForClass(editExistingEvent)}
        existingEvent={editExistingEvent}
        calendarViewStart={startRange.current}
        calendarViewEnd={endRange.current}
        onUpdated={(existingEvent, events, includeFutureEvents) => {
          if (includeFutureEvents) {
            updateInstanceAndFutureEvents(existingEvent.id as string, events);
          } else {
            updateSingleEventFromServer(existingEvent.id as string, events);
          }
          setOverlayState(undefined);
        }}
        onCancel={() => setOverlayState(undefined)}
    />}

    {overlayState == 'eventresize' && resizingEvent && <ConfirmEditPrompt
        isDelete={false}
        onConfirm={updateFutureEvents => {
          sendUpdateRequest(resizingEvent, updateFutureEvents)
          setOverlayState(undefined)
        }}
        onCancel={() => setOverlayState(undefined)}/>}

  </div>
};
